// CreateActionDialog — modal form for creating a new action.
// Actions require an ownership role (unlike events which use recorded_by).

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { actionsApi, type CreateActionRequest } from '@/lib/api/actions'
import { ownershipRolesApi } from '@/lib/api/ownershipRoles'
import { queryKeys } from '@/lib/api/queryKeys'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { X } from 'lucide-react'

interface CreateActionDialogProps {
  categoryId: string
  categoryName: string
  open: boolean
  onClose: () => void
  onCreated: (actionId: string) => void
}

export function CreateActionDialog({
  categoryId,
  categoryName,
  open,
  onClose,
  onCreated,
}: CreateActionDialogProps) {
  const api = useApi()
  const { slug } = useTenant()
  const queryClient = useQueryClient()
  const actApi = actionsApi(api)
  const rolesApi = ownershipRolesApi(api)

  // Fetch ownership roles for the dropdown
  const { data: ownershipRoles } = useQuery({
    queryKey: queryKeys.ownershipRoles.all(slug),
    queryFn: () => rolesApi.list(),
    enabled: open,
  })

  // Form state
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [ownerRoleId, setOwnerRoleId] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [error, setError] = useState<string | null>(null)

  const selectedRoleId = ownerRoleId || ownershipRoles?.[0]?.id || ''

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!title.trim()) throw new Error('Action title is required')
      if (!selectedRoleId) throw new Error('Owner role is required')

      const req: CreateActionRequest = {
        title: title.trim(),
        category_id: categoryId,
        owner_role_id: selectedRoleId,
      }
      if (description.trim()) req.description = description.trim()
      if (dueDate) req.due_date = dueDate

      return actApi.create(req)
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.actions.all(slug) })
      queryClient.invalidateQueries({ queryKey: queryKeys.categories.tree(slug, 'action') })
      resetForm()
      onCreated(result.id)
    },
    onError: (err: Error) => {
      setError(err.message)
    },
  })

  function resetForm() {
    setTitle('')
    setDescription('')
    setOwnerRoleId('')
    setDueDate('')
    setError(null)
  }

  function handleClose() {
    resetForm()
    onClose()
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="mx-4 w-full max-w-md rounded-lg border bg-background shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold">New Action</h2>
            <p className="text-xs text-muted-foreground">in {categoryName}</p>
          </div>
          <button
            className="rounded-sm p-1 hover:bg-accent"
            onClick={handleClose}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <div className="space-y-4 px-6 py-4">
          {error && (
            <div className="rounded bg-destructive/10 p-2 text-sm text-destructive">
              {error}
            </div>
          )}

          <div>
            <Label htmlFor="action-title">Title *</Label>
            <Input
              id="action-title"
              placeholder="Action title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
            />
          </div>

          <div>
            <Label htmlFor="action-description">Description</Label>
            <textarea
              id="action-description"
              className="min-h-[60px] w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="Optional description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="action-owner">Owner Role *</Label>
            <select
              id="action-owner"
              className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              value={selectedRoleId}
              onChange={(e) => setOwnerRoleId(e.target.value)}
            >
              {!ownershipRoles?.length && (
                <option value="">Loading roles...</option>
              )}
              {ownershipRoles?.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.label} — {role.owner_display_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="action-due-date">Due Date</Label>
            <Input
              id="action-due-date"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t px-6 py-4">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending || !title.trim() || !selectedRoleId}
          >
            {createMutation.isPending ? 'Creating...' : 'Create Action'}
          </Button>
        </div>
      </div>
    </div>
  )
}
